import moment, { Moment } from 'moment'
import { Date as DateLanguage, TimeFormats as TimeFormatsLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'

export type SupportedLocale = 'de_DE' | 'en_GB' | 'en_US' | 'fr_FR' | 'it_IT' | 'nl_NL' | 'pt_PT'

const LOCALE_INPUT_PATTERNS: Record<SupportedLocale, string[]> = {
    de_DE: ['D.M.YY', 'D.M.YYYY'],
    en_GB: ['D/M/YYYY', 'D/M/YY'],
    fr_FR: ['D/M/YYYY', 'D/M/YY'],
    en_US: ['M/D/YY', 'M/D/YYYY'],
    it_IT: ['D/M/YY', 'D/M/YYYY'],
    pt_PT: ['D/M/YY', 'D/M/YYYY'],
    nl_NL: ['D-M-YYYY', 'D-M-YY']
}

/**
 * Formats a numeric value that specifies a total duration in seconds to "[w]d [x]h [y]m [z]s" format.
 */
export const convertDurationInSecondsToFormattedString = (
    durationInSeconds: number,
    language: CLanguageHandler
): string => {
    language.loadBundle(TimeFormatsLanguage.NAME)

    const formatPatterns: string[] = [
        language.get(TimeFormatsLanguage.Formatpipeddurationdayshrsminssecs),
        language.get(TimeFormatsLanguage.Formatpipeddurationhrsminssecs),
        language.get(TimeFormatsLanguage.Formatpipeddurationminssecs),
        language.get(TimeFormatsLanguage.Formatpipeddurationsecs)
    ]

    // eslint-disable-next-line
    return com.Akixi.Text.CDurationAsSecs(durationInSeconds).toFormattedString(formatPatterns)
}

/**
 * Prepares a date object that is set to the same time as input but time adjusted to the start of minute.
 *
 * @param value Date object to adjust.
 */
export const adjustTimeToStartOfMinute = (value: Date): Date => moment(value).startOf('minute').toDate()

/**
 * Prepares a date object that is set to the same time as input but time adjusted to the end of minute.
 *
 * @param value Date object to adjust.
 */
export const adjustTimeToEndOfMinute = (value: Date): Date => moment(value).endOf('minute').toDate()

/**
 * Parses a text value that specifies a duration in "[w]d [x]h [y]m [z]s" format to total seconds.
 */
export const convertDurationAsFormattedStringToSeconds = (
    durationAsFormatterString: string,
    defaultDurationOnError: number
): number => {
    // eslint-disable-next-line
    const durationInSeconds: number = com.Akixi.Text.ParseFormattedDurationValue(
        durationAsFormatterString,
        defaultDurationOnError
    )
    return durationInSeconds
}

export const convertDateToDurationDateTimeString = (date: Date, language: CLanguageHandler): string => {
    const pattern: string = `${language.get(DateLanguage.DatePattern)} HH:mm:ss`
    const formattedString: string = moment(date).format(pattern)
    return formattedString
}

export const convertDateToDurationDateTimeString12HourFormat = (date: Date, language: CLanguageHandler): string => {
    const pattern: string = `${language.get(DateLanguage.DatePattern)} hh:mm:ss A`
    const formattedString: string = moment(date).format(pattern)
    return formattedString
}

export const convertDateToDateString = (date: Date, language: CLanguageHandler): string => {
    const pattern: string = `${language.get(DateLanguage.DatePattern)}`
    const formattedString: string = moment(date).format(pattern)
    return formattedString
}

export const convertDateToDurationTimeString = (date: Date): string => {
    const pattern: string = 'HH:mm:ss A' // This pattern covers both en_US and en_GB formats returned from backend.
    const formattedString: string = moment(date).format(pattern)
    return formattedString
}

export const convertDateToDurationTimeString12HourFormat = (date: Date): string => {
    const pattern: string = 'hh:mm:ss A'
    const formattedString: string = moment(date).format(pattern)
    return formattedString
}

export const convertDurationTimeStringToDate = (value: string): Date => {
    const pattern: string = 'HH:mm:ss A' // This pattern covers both en_US and en_GB formats returned from backend.
    const date: Date = moment(value, pattern).toDate()
    return date
}

export const convertDateStringToDate = (value: string, locale: SupportedLocale = 'en_GB'): Date => {
    const m: Moment = moment(value, LOCALE_INPUT_PATTERNS[locale], true)
    return m.isValid() ? m.toDate() : new Date(NaN)
}

export function convertDateToPrettyDateString(value: Date, prettyPattern: string = 'Do MMM, YYYY'): string {
    return moment(value).format(prettyPattern)
}

export const isMoreThanDaysAgo = (dateToCheck: Date, days: number): boolean =>
    moment(dateToCheck).isBefore(moment().subtract(days, 'days'))
